import React, { useCallback, useState } from 'react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import inkvoydLogo from '../assets/inkvoydLogo.png';
import LandingWaitingListModal from './LandingWaitingListModal';

interface NavItem {
  href: string;
  label: string;
}

const navItems: NavItem[] = [
  { href: '#features', label: 'Features' },
  { href: '#how', label: 'How it works' },
  { href: '#faq', label: 'FAQ' },
];

const SIGN_UP_BUTTON_CLASS =
  'px-5 py-3 rounded-2xl shadow font-semibold bg-white text-[#2f2f2d] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]';

/**
 * Landing header. Renders the brand, the desktop nav, a desktop sign-up CTA,
 * and an accessible mobile menu toggle that exposes the same nav plus a
 * small-breakpoint sign-up CTA (previously both were `hidden md:*`).
 */
const LandingHeader: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((open) => !open);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <header className="mx-auto max-w-6xl px-5 py-4 text-[#2f2f2d]">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img
            src={inkvoydLogo}
            alt="InkVoyd Logo"
            className="h-8 w-8 rounded-xl bg-white/80"
          />
          <span className="text-xl font-extrabold tracking-tight">InkVoyd</span>
        </div>

        <nav aria-label="Main" className="hidden gap-6 text-sm md:flex">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="font-bold hover:underline focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <LandingWaitingListModal
            title="Sign up for InkVoyd"
            buttonText="Sign up"
            buttonClassName={SIGN_UP_BUTTON_CLASS}
          />
        </div>

        <button
          type="button"
          onClick={toggleMenu}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation"
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          className="inline-flex items-center justify-center rounded-lg p-2 text-[#2f2f2d] hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d] md:hidden"
        >
          {isMenuOpen ? (
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {isMenuOpen && (
        <div
          id="mobile-navigation"
          className="mt-4 border-t border-[#2f2f2d]/10 pt-4 md:hidden"
        >
          <nav aria-label="Mobile">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={closeMenu}
                    className="block rounded-lg px-3 py-2 font-semibold hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-[#2f2f2d]"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <LandingWaitingListModal
                  title="Sign up for InkVoyd"
                  buttonText="Sign up"
                  buttonClassName={`w-full ${SIGN_UP_BUTTON_CLASS}`}
                />
              </li>
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default LandingHeader;
